import { useState } from 'react';
import { Search, Filter, X, Calendar, Flag, CheckCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

export interface TodoFiltersState {
  search: string;
  priority: string[];
  status: string[];
  deadline: 'all' | 'overdue' | 'today' | 'week' | 'none';
  showCompleted: boolean;
}

interface TodoFiltersProps {
  filters: TodoFiltersState;
  onFiltersChange: (filters: TodoFiltersState) => void;
}

const PRIORITY_OPTIONS = [
  { value: 'urgent', label: 'Urgent', color: 'bg-destructive text-destructive-foreground' },
  { value: 'high', label: 'Hoog', color: 'bg-destructive/80 text-destructive-foreground' },
  { value: 'medium', label: 'Middel', color: 'bg-warning text-warning-foreground' },
  { value: 'low', label: 'Laag', color: 'bg-muted text-muted-foreground' },
];

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'Bezig' },
  { value: 'blocked', label: 'Geblokkeerd' },
  { value: 'completed', label: 'Voltooid' },
  { value: 'cancelled', label: 'Geannuleerd' },
];

const DEADLINE_OPTIONS = [
  { value: 'all', label: 'Alle deadlines' },
  { value: 'overdue', label: 'Verlopen' },
  { value: 'today', label: 'Vandaag' },
  { value: 'week', label: 'Deze week' },
  { value: 'none', label: 'Geen deadline' },
];

export function TodoFilters({ filters, onFiltersChange }: TodoFiltersProps) {
  const [isOpen, setIsOpen] = useState(false);

  const activeFilterCount = 
    filters.priority.length + 
    filters.status.length + 
    (filters.deadline !== 'all' ? 1 : 0) +
    (filters.showCompleted ? 1 : 0);

  const updateFilter = <K extends keyof TodoFiltersState>(
    key: K, 
    value: TodoFiltersState[K]
  ) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const toggleArrayFilter = (key: 'priority' | 'status', value: string) => {
    const current = filters[key];
    const updated = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    updateFilter(key, updated);
  };

  const clearFilters = () => {
    onFiltersChange({
      search: '',
      priority: [],
      status: [],
      deadline: 'all',
      showCompleted: false,
    });
  };

  return (
    <div className="flex flex-col sm:flex-row gap-3">
      {/* Search Input */}
      <div className="relative flex-1 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Zoeken op titel of beschrijving..."
          value={filters.search}
          onChange={(e) => updateFilter('search', e.target.value)}
          className="pl-9"
        />
        {filters.search && (
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
            onClick={() => updateFilter('search', '')}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      {/* Filter Popover */}
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="gap-2">
            <Filter className="h-4 w-4" />
            Filters
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 w-5 p-0 flex items-center justify-center text-xs">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="end">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Filters</h4>
              {activeFilterCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  Wis alles
                </Button>
              )}
            </div>

            {/* Priority Filter */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm font-medium">
                <Flag className="h-4 w-4" />
                Prioriteit
              </Label>
              <div className="flex flex-wrap gap-2">
                {PRIORITY_OPTIONS.map((option) => (
                  <Badge
                    key={option.value}
                    variant={filters.priority.includes(option.value) ? 'default' : 'outline'}
                    className={`cursor-pointer ${filters.priority.includes(option.value) ? option.color : ''}`}
                    onClick={() => toggleArrayFilter('priority', option.value)}
                  >
                    {option.label}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Status Filter */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm font-medium">
                <CheckCircle className="h-4 w-4" />
                Status
              </Label>
              <div className="flex flex-wrap gap-2">
                {STATUS_OPTIONS.map((option) => (
                  <Badge
                    key={option.value}
                    variant={filters.status.includes(option.value) ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => toggleArrayFilter('status', option.value)}
                  >
                    {option.label}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Deadline Filter */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm font-medium">
                <Calendar className="h-4 w-4" />
                Deadline
              </Label>
              <Select
                value={filters.deadline}
                onValueChange={(value: TodoFiltersState['deadline']) => updateFilter('deadline', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DEADLINE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Show Completed Toggle */}
            <div className="flex items-center gap-2 pt-2 border-t">
              <Checkbox
                id="showCompleted"
                checked={filters.showCompleted}
                onCheckedChange={(checked) => updateFilter('showCompleted', checked as boolean)}
              />
              <Label htmlFor="showCompleted" className="text-sm cursor-pointer">
                Toon voltooide taken
              </Label>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Active filter badges (visible on larger screens) */}
      {activeFilterCount > 0 && (
        <div className="hidden md:flex items-center gap-2 flex-wrap">
          {filters.priority.map((p) => (
            <Badge key={p} variant="secondary" className="gap-1">
              {PRIORITY_OPTIONS.find(o => o.value === p)?.label}
              <X className="h-3 w-3 cursor-pointer" onClick={() => toggleArrayFilter('priority', p)} />
            </Badge>
          ))}
          {filters.status.map((s) => (
            <Badge key={s} variant="secondary" className="gap-1">
              {STATUS_OPTIONS.find(o => o.value === s)?.label}
              <X className="h-3 w-3 cursor-pointer" onClick={() => toggleArrayFilter('status', s)} />
            </Badge>
          ))}
          {filters.deadline !== 'all' && (
            <Badge variant="secondary" className="gap-1">
              {DEADLINE_OPTIONS.find(o => o.value === filters.deadline)?.label}
              <X className="h-3 w-3 cursor-pointer" onClick={() => updateFilter('deadline', 'all')} />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}

export const defaultFilters: TodoFiltersState = {
  search: '',
  priority: [],
  status: [],
  deadline: 'all',
  showCompleted: false,
};

// Filter helper function
export function applyTodoFilters<T extends {
  title: string;
  description?: string | null;
  priority: string;
  status: string;
  due_date?: string | null;
}>(todos: T[], filters: TodoFiltersState): T[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekFromNow = new Date(today);
  weekFromNow.setDate(weekFromNow.getDate() + 7);

  return todos.filter((todo) => {
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesTitle = todo.title.toLowerCase().includes(searchLower);
      const matchesDesc = todo.description?.toLowerCase().includes(searchLower);
      if (!matchesTitle && !matchesDesc) return false;
    }

    // Priority filter
    if (filters.priority.length > 0 && !filters.priority.includes(todo.priority)) {
      return false;
    }

    // Status filter
    if (filters.status.length > 0 && !filters.status.includes(todo.status)) {
      return false;
    }

    // Show completed toggle
    if (!filters.showCompleted && todo.status === 'completed') {
      return false;
    }

    // Deadline filter
    if (filters.deadline !== 'all') {
      const dueDate = todo.due_date ? new Date(todo.due_date) : null;
      
      switch (filters.deadline) {
        case 'overdue':
          if (!dueDate || dueDate >= today) return false;
          break;
        case 'today':
          if (!dueDate) return false;
          const todoDay = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
          if (todoDay.getTime() !== today.getTime()) return false;
          break;
        case 'week':
          if (!dueDate || dueDate < today || dueDate > weekFromNow) return false;
          break;
        case 'none':
          if (dueDate) return false;
          break;
      }
    }

    return true;
  });
}
